/**
 * Password Reset Modal Component
 *
 * Two-step process:
 * 1. Enter email to request reset link
 * 2. Success message with instructions
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import MaterialIcon from '../MaterialIcon';
import './AuthModals.css';

const PasswordResetModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const { requestPasswordReset } = useAuth();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await requestPasswordReset(email);

      if (result.success) {
        setSuccess(true);
      } else {
        // For security, always show success message even if email doesn't exist
        // This prevents email enumeration attacks
        setSuccess(true);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  // Success state
  if (success) {
    return (
      <div className="auth-modal-overlay" onClick={handleClose}>
        <div className="auth-modal auth-modal-success" onClick={(e) => e.stopPropagation()}>
          <div className="auth-success-content">
            <MaterialIcon name="email" className="auth-success-icon" />
            <h2>Check Your Email</h2>
            <p>
              If an account exists for <strong>{email}</strong>, you'll receive a password reset
              link shortly.
            </p>
            <p className="auth-success-subtext">
              The link will expire in 24 hours. Check your spam folder if you don't see it.
            </p>
            <button className="auth-submit-button" onClick={handleClose}>
              <MaterialIcon name="check" />
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="auth-modal-header">
          <h2>Reset Password</h2>
          <button className="auth-modal-close" onClick={handleClose} aria-label="Close modal">
            <MaterialIcon name="close" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="auth-modal-form">
          <p className="auth-reset-description">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          {error && (
            <div className="auth-error-message">
              <MaterialIcon name="error" />
              <span>{error}</span>
            </div>
          )}

          {/* Email Field */}
          <div className="auth-form-group">
            <label htmlFor="reset-email">Email</label>
            <div className="auth-input-wrapper">
              <MaterialIcon name="email" />
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="email"
                disabled={loading}
                autoFocus
              />
            </div>
          </div>

          {/* Submit Button */}
          <button type="submit" className="auth-submit-button" disabled={loading || !email}>
            {loading ? (
              <>
                <span className="auth-spinner" />
                Sending...
              </>
            ) : (
              <>
                <MaterialIcon name="send" />
                Send Reset Link
              </>
            )}
          </button>
        </form>

        {/* Footer - Switch to Login */}
        <div className="auth-modal-footer">
          <p>
            Remember your password?{' '}
            <button className="auth-link-button" onClick={onSwitchToLogin} disabled={loading}>
              Log in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

PasswordResetModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSwitchToLogin: PropTypes.func.isRequired,
};

export default PasswordResetModal;
